import React from "react";
import { CardBody } from "reactstrap";
import { Line } from "react-chartjs-2";
import * as Styled from "./LineGraph.styles";
import moment from "moment/moment";
import l10n from "../../localization";
import theme from "../../theme/default";
import Helpers from "../../helpers/helpers";

export default function LineGraph({ meters, meterSiteCode, meterCode }) {
  const filteredBySiteCode = meters.meterReadingsByDay.result.filter(
    (meterReadingByDay) => meterReadingByDay.meterSiteCode === meterSiteCode
  );

  let readings = [];

  if (filteredBySiteCode.length > 0) {
    const showingMeter = filteredBySiteCode[0].meters.filter(
      (fMeterSideCode) => fMeterSideCode.meterCode === meterCode
    );

    if (showingMeter.length > 0) {
      if (showingMeter[0].readings && showingMeter[0].readings.length > 0) {
        readings = [...showingMeter[0].readings];
      }
    }
  }

  readings = readings.reverse();

  const increasedConsumptions = readings.filter(
    (reading) => reading.isIncrConsumption === true
  );

  const mainIncreasedConsumptions = readings.filter(
    (reading) => reading.isIncrConsumptionMainMeter === true
  );

  const data = (canvas) => {
    const ctx = canvas.getContext("2d");
    const gradient = ctx.createLinearGradient(0, 0, 0, 300);
    gradient.addColorStop(0, "rgba(13, 157, 219, 0.5)");
    gradient.addColorStop(1, "rgba(13, 157, 219, 0)");

    const dates = readings.map((reading) => {
      return moment(reading.date).format("DD.MM.YY");
    });

    return {
      labels: dates,
      datasets: [
        {
          label: "Main Increased",
          borderColor: "rgba(255, 255, 255, 0)",
          backgroundColor: "rgba(255, 255, 255, 0",
          data: readings.map((reading) => {
            return reading.isIncrConsumptionMainMeter &&
              !reading.isIncrConsumption
              ? reading.accountedConsumption
              : null;
          }),
          pointBorderColor: theme.colors[40],
          pointBackgroundColor: theme.colors[40],
          pointBorderWidth: 2,
          pointHoverRadius: 6,
          pointHoverBorderWidth: 2,
          pointHoverBackgroundColor: "#fff",
          pointRadius: 6,
          pointHitRadius: 10,
        },
        {
          label: "Increased",
          borderColor: "rgba(255, 255, 255, 0)",
          backgroundColor: "rgba(255, 255, 255, 0",
          data: readings.map((reading) => {
            return reading.isIncrConsumption &&
              !reading.isIncrConsumptionMainMeter
              ? reading.accountedConsumption
              : null;
          }),
          pointBorderColor: "red",
          pointBackgroundColor: "#fff",
          pointBorderWidth: 2,
          pointHoverRadius: 6,
          pointHoverBorderWidth: 2,
          pointHoverBackgroundColor: "#fff",
          pointRadius: 6,
          pointHitRadius: 10,
        },
        {
          label: "Both",
          borderColor: "rgba(255, 255, 255, 0)",
          backgroundColor: "rgba(255, 255, 255, 0",
          data: readings.map((reading) => {
            return reading.isIncrConsumption &&
              reading.isIncrConsumptionMainMeter
              ? reading.accountedConsumption
              : null;
          }),
          pointBorderColor: "red",
          pointBackgroundColor: theme.colors[41],
          pointBorderWidth: 2,
          pointHoverRadius: 6,
          pointHoverBorderWidth: 2,
          pointHoverBackgroundColor: "#fff",
          pointRadius: 6,
          pointHitRadius: 10,
        },
        {
          label: "Consumptions",
          borderColor: "#109CF1",
          borderWidth: "2",
          backgroundColor: gradient,
          data: readings.map((reading) => {
            return reading.accountedConsumption;
          }),
          pointBorderColor: "#109CF1",
          pointBackgroundColor: "#109CF1",
          pointBorderWidth: 2,
          pointHitRadius: 15,
          pointHoverBackgroundColor: "#109CF1",
          pointHoverBorderWidth: 4,
        },
      ],
    };
  };

  return (
    <Styled.Card>
      <CardBody style={{ padding: "5px" }}>
        <Styled.GraphLegend>
          {increasedConsumptions.length > 0 && (
            <Styled.LegendItem>
              <Styled.LegendItemMark />
              <Styled.LegendItemText>
                {l10n.graphList.increasedConsumption}
              </Styled.LegendItemText>
            </Styled.LegendItem>
          )}
          {mainIncreasedConsumptions.length > 0 && (
            <Styled.LegendItem>
              <Styled.LegendItemMark color={theme.colors[40]} />
              <Styled.LegendItemText>
                {l10n.graphList.increasedConsumptionMain}
              </Styled.LegendItemText>
            </Styled.LegendItem>
          )}
        </Styled.GraphLegend>
        <Line
          options={{
            tooltips: {
              displayColors: false,
              backgroundColor: "rgba(25, 42, 62, 0.75)",
              enabled: true,
              mode: "label",
              bodyFontSize: 13,
              bodyFontColor: "white",
              yPadding: 6,
              xPadding: 8,
              cornerRadius: 12,
              callbacks: {
                title: (tooltipItems) => {
                  return tooltipItems[0].xLabel;
                },
                afterLabel: (tooltipItems) => {
                  if (tooltipItems.datasetIndex === 3) {
                    let meterCode = "";
                    if (readings.length > tooltipItems.index) {
                      meterCode = readings[tooltipItems.index].meterCode;
                    }
                    return `${l10n.reports.meterCode}: ${meterCode}`;
                  }

                  return;
                },
                label: (tooltipItems, data) => {
                  if (tooltipItems.datasetIndex === 3) {
                    let value = tooltipItems.yLabel;

                    if (!isNaN(value)) {
                      value = Helpers.formatNumber(value);
                    }

                    return `${l10n.reports.accountedConsumption}: ${value} m3`;
                  }

                  return;
                },
              },
            },
            legend: {
              display: false,
            },
            layout: {
              padding: {
                left: 0,
                right: 0,
                top: 0,
                bottom: 0,
              },
            },
            scales: {
              yAxes: [
                {
                  gridLines: {
                    drawBorder: false,
                    borderDash: [10, 10],
                    color: "#c4c4c4",
                    zeroLineBorderDash: [10, 10],
                    zeroLineColor: "#c4c4c4",
                  },
                  ticks: {
                    fontColor: "#4C5862",
                    fontSize: 12,
                    min: 0,
                    padding: 8,
                  },
                },
              ],
              xAxes: [
                {
                  gridLines: {
                    display: false,
                    min: 0,
                  },
                  ticks: {
                    fontColor: "#4C5862",
                    fontSize: 12,
                    autoSkip: true,
                    maxRotation: 0,
                    minRotation: 0,
                    min: 0,
                    autoSkipPadding: 30,
                    padding: 10,
                  },
                },
              ],
            },
            maintainAspectRatio: false,
            animation: false,
          }}
          data={data}
        />
      </CardBody>
    </Styled.Card>
  );
}
